import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Activity, Award, Users, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import HeroServiceCarousel from './HeroServiceCarousel';

// ── CountUp hook ──────────────────────────────────────────────────────────────
/**
 * useCountUp
 * Counts from 0 → target whenever the element enters the viewport.
 * Memoised via ref so it only triggers once.
 */
function useCountUp(target, duration = 1800) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const startTime = performance.now();
          const tick = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // Ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(tick);
            else setCount(target);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [target, duration]);

  return { count, ref };
}

// ── StatItem ──────────────────────────────────────────────────────────────────
const StatItem = ({ icon: Icon, value, suffix = '+', label, delay = 0 }) => {
  const { count, ref } = useCountUp(value);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className="flex items-center gap-3 bg-white/70 backdrop-blur-sm px-3 py-2.5 rounded-xl border border-white/80 shadow-sm hover:shadow-md transition-shadow duration-300"
    >
      <div className="p-2 bg-rose-50 rounded-xl text-[#800000] shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xl font-extrabold text-slate-900 leading-none">
          {count}{suffix}
        </p>
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mt-0.5">
          {label}
        </p>
      </div>
    </motion.div>
  );
};

// ── Floating decorative blob ──────────────────────────────────────────────────
const Blob = ({ className, style }) => (
  <div
    aria-hidden="true"
    className={`absolute rounded-full blur-3xl pointer-events-none ${className}`}
    style={style}
  />
);

// ── Main HeroSection component ────────────────────────────────────────────────
const HeroSection = () => {
  const { t } = useLanguage();

  // Staggered text animation variants
  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  return (
    <section
      id="home"
      className="relative min-h-[92vh] lg:min-h-screen flex items-center pt-20 pb-12 overflow-hidden"
      data-testid="hero-section"
      style={{
        background:
          'linear-gradient(135deg, #f8fafc 0%, #f0f4ff 40%, #f0f9ff 100%)',
      }}
    >
      {/* ── Decorative background blobs ──────────────────────────────────── */}
      <Blob
        className="w-[480px] h-[480px] bg-slate-100/70 -top-24 -right-24 z-0"
      />
      <Blob
        className="w-[320px] h-[320px] bg-blue-100/50 bottom-0 -left-20 z-0"
      />
      <Blob
        className="w-[200px] h-[200px] bg-[#800000]/8 top-1/2 left-1/3 z-0"
      />

      {/* ── Floating decorative shapes ───────────────────────────────────── */}
      <motion.div
        aria-hidden="true"
        className="absolute top-32 right-[48%] w-3 h-3 bg-[#800000]/20 rounded-full hidden lg:block"
        animate={{ y: [0, -12, 0] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute bottom-28 right-[42%] w-2 h-2 bg-rose-400/40 rounded-full hidden lg:block"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute top-48 left-[52%] w-5 h-5 border-2 border-[#800000]/15 rounded-sm rotate-45 hidden lg:block"
        animate={{ rotate: [45, 90, 45] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* ── Grid container ───────────────────────────────────────────────── */}
      <div className="container mx-auto px-5 sm:px-8 lg:px-12 relative z-10 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-8 xl:gap-14">

          {/* ════════════════════════════════════════════════════════════════
              LEFT COLUMN — Hero copy (45%)
          ═══════════════════════════════════════════════════════════════ */}
          <motion.div
            className="flex-1 lg:max-w-[45%] w-full"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >

            {/* Trust badge */}
            <motion.div
              variants={itemVariants}
              className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/90 backdrop-blur-sm shadow-sm border border-slate-200 mb-7"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <span className="text-[#800000] font-bold text-xs uppercase tracking-widest">
                GastroCare Bhopal
              </span>
            </motion.div>

            {/* Heading */}
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.08] mb-5"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {t('hero_title')}
              <span
                className="block mt-1"
                style={{
                  background: 'linear-gradient(90deg, #800000 0%, #b91c1c 60%, #dc2626 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('hero_subtitle')}
              </span>
            </motion.h1>

            {/* Description */}
            <motion.p
              variants={itemVariants}
              className="text-base md:text-lg text-slate-600 mb-6 max-w-lg leading-relaxed mt-1"
            >
              {t('hero_description')}
            </motion.p>

            {/* CTA Buttons — exact originals preserved */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 mt-3 mb-7"
            >
              {/* Start Journey → /contact */}
              <Link to="/contact">
                <Button
                  className="group bg-[#800000] hover:bg-black text-white h-14 lg:h-14 px-9 rounded-full flex items-center justify-center gap-3 text-base font-bold transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-0.5"
                >
                  {t('hero_cta')}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </Link>

              {/* Call Now → tel */}
              <a href="tel:+917554055123" className="block">
                <Button
                  variant="outline"
                  className="border-2 border-slate-200 bg-white/80 backdrop-blur-sm text-slate-900 hover:border-[#800000] hover:text-[#800000] h-14 lg:h-14 px-9 rounded-full flex items-center justify-center gap-3 text-base font-bold w-full transition-all duration-300 hover:-translate-y-0.5"
                >
                  <Phone className="w-5 h-5" />
                  {t('hero_call')}
                </Button>
              </a>
            </motion.div>

            {/* ── Statistics with CountUp ────────────────────────────── */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-2 gap-2 mt-3"
            >
              <StatItem
                icon={Activity}
                value={100}
                suffix="+"
                label="Advanced Beds"
                delay={0.3}
              />
              <StatItem
                icon={Award}
                value={15}
                suffix="+"
                label="Medical Experts"
                delay={0.45}
              />
              <StatItem
                icon={Users}
                value={5000}
                suffix="+"
                label="Lives Transformed"
                delay={0.6}
              />
              <StatItem
                icon={Star}
                value={98}
                suffix="%"
                label="Patient Satisfaction"
                delay={0.75}
              />
            </motion.div>
          </motion.div>

          {/* ════════════════════════════════════════════════════════════════
              RIGHT COLUMN — Service Carousel (55%)
          ═══════════════════════════════════════════════════════════════ */}
          <motion.div
            className="flex-1 lg:max-w-[55%] w-full max-w-md mx-auto lg:mx-0"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <HeroServiceCarousel />
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;